// SPDX-FileCopyrightText: 2026 Bundesrepublik Deutschland, vertreten durch das BMI/ITZBund
//
// SPDX-License-Identifier: GPL-3.0-or-later

/*
 * This file is part of the package itzbund/gsb-places of the GSB 11 Project by ITZBund
 *
 * Copyright (C) 2025 Bundesrepublik Deutschland, vertreten durch das
 * BMI/ITZBund. Author: Thomas Maroschik
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

// This configuration object determines which deck.gl classes are accessible in json
import {MapView, View, Layer, PickingInfo} from '@deck.gl/core';
import * as Layers from '@deck.gl/layers';
import * as AggregationLayers from '@deck.gl/aggregation-layers';
import * as GeoLayers from '@deck.gl/geo-layers';
import * as MeshLayers from '@deck.gl/mesh-layers';
import {CARTO_LAYERS, colorBins, colorCategories, colorContinuous} from '@deck.gl/carto';
import {CARTO_SOURCES} from '@carto/api-client';
import * as Widgets from '@deck.gl/widgets';
import {FeaturePopupWidget} from "./feature-popup-widget";

const IntlNumberFormat= ({options, number}) => (info: PickingInfo) => {
  return Intl.NumberFormat(undefined, options).format(typeof number === 'function' ? number(info) : number);
}

export default {
  // Classes that should be instantiatable by JSON converter
  classes: Object.assign(
    {MapView},
    // a map of all layers that should be exposes as JSONLayers
    Layers,
    AggregationLayers,
    CARTO_LAYERS,
    GeoLayers,
    MeshLayers,
    // Support `@deck.gl/widgets` Widgets
    Widgets,
    {FeaturePopupWidget}
  ),

  // Functions that should be executed by JSON converter
  functions: {...CARTO_SOURCES, IntlNumberFormat, colorBins, colorCategories, colorContinuous},

  postProcessConvertedJson: json => {
    // Filter out invalid props. Typically, props will be invalid while the user is typing.
    if (json.layers) {
      json.layers = json.layers.filter(layer => layer instanceof Layer);
    }
    if (json.widgets) {
      json.widgets = json.widgets.filter(widget => typeof widget.onAdd === 'function');
    }
    if (json.views && !(json.views instanceof View)) {
      json.views = json.views.filter(view => view instanceof View);
    }
    return json;
  }
};
